"use client";

import { useState, useEffect } from 'react';
import { Search, RefreshCw, Check, X, Eye, Upload, AlertCircle, ExternalLink, Download, Filter, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ActivityIcon, ShieldBan, DownloadIcon, FileDown } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import PDFMergeUploader from '../../components/PDFMergeUploader';

// Filter dropdown component
const FilterDropdown = ({ 
  documentType, 
  filterState, 
  onFilterChange,
  isOpen,
  onToggle 
}) => {


  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors"
        title="Filter column"
      >
        <Filter className="h-3 w-3" />
        <ChevronDown className="h-3 w-3" />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border rounded-md shadow-lg z-20 min-w-40">
          <div className="p-3 space-y-2">
            <div className="text-xs font-medium text-gray-700 mb-2">Filter by:</div>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={filterState.hasDocument}
                onChange={(e) => onFilterChange(documentType, 'hasDocument', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>Has Document</span>
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={filterState.noDocument}
                onChange={(e) => onFilterChange(documentType, 'noDocument', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>No Document</span>
            </label>
            <div className="pt-2 border-t">
              <button
                onClick={() => {
                  onFilterChange(documentType, 'hasDocument', false);
                  onFilterChange(documentType, 'noDocument', false);
                }}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Clear Filter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Modal component for viewing documents
const ViewDocumentModal = ({ 
    isOpen, 
    onClose, 
    documentUrl,
    docMetadata,
    setDocuments,
  }) => {
    const [iframeError, setIframeError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
  
    useEffect(() => {
      if (isOpen) {
        setIframeError(false);
        setIsLoading(true);
      }
    }, [isOpen, documentUrl]);
  
    const handleIframeError = () => {
      setIframeError(true);
      setIsLoading(false);
    };
  
    const handleIframeLoad = () => {
      setIsLoading(false);
    };
  
    const getFileExtension = (url) => {
      try {
        const pathname = new URL(url).pathname;
        return pathname.split('.').pop()?.toLowerCase() || '';
      } catch {
        return '';
      }
    };
  
    const isEmbeddableUrl = (url) => {
      if (url.includes('drive.google.com/file/d/') && url.includes('/preview')) {
        return true;
      }
      
      if ((url.includes('docs.google.com') || url.includes('sheets.google.com') || url.includes('slides.google.com')) 
          && url.includes('/edit')) {
        return true;
      }
      
      const fileExt = getFileExtension(url);
      const embeddableExtensions = ['pdf', 'txt', 'csv', 'xml', 'json'];
      
      if (embeddableExtensions.includes(fileExt)) {
        return true;
      }
      
      return false;
    };
  
    const getEmbeddableUrl = (url) => {
      if (url.includes('drive.google.com')) {
        return convertGoogleDriveUrl(url);
      }
      
      const fileExt = getFileExtension(url);
      const viewableExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
      
      if (viewableExtensions.includes(fileExt)) {
        return `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
      }
      return url;
    };
  
    const convertGoogleDriveUrl = (url) => {
      try {
        let fileId = '';
        
        if (url.includes('/file/d/')) {
          fileId = url.split('/file/d/')[1].split('/')[0];
        }
        else if (url.includes('open?id=')) {
          fileId = url.split('open?id=')[1].split('&')[0];
        }
        else if (url.includes('/document/d/')) {
          fileId = url.split('/document/d/')[1].split('/')[0];
        }
        else if (url.includes('/spreadsheets/d/')) {
          fileId = url.split('/spreadsheets/d/')[1].split('/')[0];
        }
        else if (url.includes('/presentation/d/')) {
          fileId = url.split('/presentation/d/')[1].split('/')[0];
        }

        if (fileId) {
          return `https://drive.google.com/file/d/${fileId}/preview`;
        }
      } catch (error) {
        console.error('Error converting Google Drive URL:', error);
      }
      
      return url;
    };
  
    const openInNewTab = () => {
      window.open(documentUrl, '_blank', 'noopener,noreferrer');
    };
  
    const downloadDocument = () => {
      const link = document.createElement('a');
      link.href = documentUrl;
      link.download = documentUrl.split('/').pop() || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    const handleDeleteDocument = async() => {
      try {
        const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/emp-documnets/employee-documents`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify({
            employee_key: docMetadata.employee_key,
            [docMetadata.docType]: false
          })
        });
        if (!response.ok) {
          throw new Error('Failed to delete document');
        }
        setDocuments(docMetadata.eid, docMetadata.docType);
        onClose();
        console.log('Document deleted successfully');
      } catch (error) {
        console.error('Error deleting document:', error);
      }
    };
  
    const renderContent = () => {
      if (iframeError || !isEmbeddableUrl(documentUrl)) {
        return (
          <div className="flex flex-col items-center justify-center h-[70vh] text-center p-8">
            <AlertCircle className="h-16 w-16 text-yellow-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Document Cannot Be Displayed
            </h3>
            <p className="text-gray-600 mb-6 max-w-md">
              This document cannot be embedded due to security restrictions. 
              You can still view or download it using the options below.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={openInNewTab}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                Open in New Tab
              </button>
              <button
                onClick={downloadDocument}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <Download className="h-4 w-4" />
                Download
              </button>
            </div>
          </div>
        );
      }
  
      return (
        <div className="relative">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}
          <iframe
            src={getEmbeddableUrl(documentUrl)}
            className="w-full h-[70vh] border rounded"
            title="Document Viewer"
            allowFullScreen
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          />
        </div>
      );
    };
  
    if (!isOpen) return null;
  
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
          <div className="flex justify-between items-center p-4 border-b">
            <h2 className="text-xl font-semibold">Document Viewer</h2>
            <div className="flex items-center gap-2">
              {!iframeError && (
                <>
                  <button
                    onClick={openInNewTab}
                    className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100"
                    title="Open in new tab"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </button>
                  <button
                    onClick={downloadDocument}
                    className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100"
                    title="Download"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </>
              )}
              <button 
                onClick={onClose} 
                className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          <div className="flex-1 p-4 overflow-auto">
            {renderContent()}
          </div>
          
          <div className="p-4 border-t flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {iframeError ? 'External viewing options available' : 'Document loaded successfully'}
            </div>
            {/* want to delete this document or replace new updated document */}
            <div className="flex gap-2">
            {/* <button className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2">Replace</button> */}
            <button onClick={handleDeleteDocument} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2">Delete this document</button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Close
            </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

// Modal component for uploading documents
const UploadDocumentModal = ({ 
  isOpen, 
  onClose, 
  onUpload,
  employeeId,
  documentType 
}) => {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      window.alert('Please upload a PDF file');
      return;
    }
    setFile(file);
    handleSubmit(file);
  };

  const handleSubmit = async (file) => {
    if (!file) return;
    
    setIsUploading(true);
    try {
      await onUpload(file);
      onClose();
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Upload Document</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-6 w-6" />
          </button>
        </div>
        <form className="p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              Uploading for: <span className="font-medium">{employeeId}</span>
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Document Type: <span className="font-medium">
                {documentType.split('_').map(word => 
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                ).join(' ')}
              </span>
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File
            </label>
            <PDFMergeUploader
              onUpload={handleFileChange}
            />
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isUploading}
            >
              Cancel
            </button>
            {/* <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              disabled={!file || isUploading}
            >
              {isUploading ? 'Uploading...' : 'Upload'}
            </button> */}
          </div>
        </form>
      </div>
    </div>
  );
};

// Pagination component
const PaginationControls = ({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange
}) => {
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  if (totalItems === 0) return null;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border-t bg-gray-50">
      <div className="flex items-center gap-2 justify-center sm:justify-start">
        <label className="text-sm text-gray-700">Show:</label>
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <span className="text-sm text-gray-700">entries</span>
      </div>

      <div className="text-sm text-gray-700 text-center sm:text-right">
        Showing {startIndex + 1} to {endIndex} of {totalItems} entries
      </div>

      <div className="flex items-center justify-center flex-wrap gap-1">
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          title="First page"
        >
          <ChevronsLeft className="h-4 w-4" />
        </button>

        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          title="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {getPageNumbers().map((page, index) => (
          <button
            key={index}
            onClick={() => typeof page === "number" ? onPageChange(page) : null}
            disabled={page === "..."}
            className={`px-3 py-2 rounded border text-sm min-w-[40px] transition-colors ${
              page === currentPage
                ? "bg-blue-600 text-white border-blue-600"
                : page === "..."
                ? "border-transparent cursor-default text-gray-400"
                : "border-gray-300 hover:bg-gray-50"
            }`}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          title="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>

        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          title="Last page"
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default function EmployeeCheckList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [viewModal, setViewModal] = useState({ isOpen: false, url: '', docMetadata: {} });
  const [uploadModal, setUploadModal] = useState({ 
    isOpen: false, 
    employeeId: '', 
    documentType: '' 
  });
  const [activeDocuments, setActiveDocuments] = useState([]);
  const [inactiveDocuments, setInactiveDocuments] = useState([]);
  
  const [filters, setFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const pathLocation = useLocation();

  const [downloadStatus, setDownloadStatus] = useState({ 
    loading: false, 
    progress: 0, 
    employee_key: "" 
  });

  // CSV Export function
  const exportToCSV = () => {
    const filteredData = getFilteredDocuments();
    
    const hasActiveFilters = Object.values(filters).some(filter => 
      filter.hasDocument || filter.noDocument
    );

    let columnsToInclude = ['eid', 'employee_name'];
    
    if (hasActiveFilters) {
      Object.entries(filters).forEach(([docType, filter]) => {
        if (filter.hasDocument || filter.noDocument) {
          columnsToInclude.push(docType);
        }
      });
    } else {
      columnsToInclude = [...columnsToInclude, ...documentTypes];
    }

    const headers = columnsToInclude.map(col => {
      if (col === 'eid') return 'Employee ID';
      if (col === 'employee_name') return 'Employee Name';
      return col.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
    });

    const rows = filteredData.map(doc => {
      return columnsToInclude.map(col => {
        if (col === 'eid' || col === 'employee_name') {
          return doc[col];
        }
        const hasDocument = Boolean(doc[col]);
        return hasDocument ? 'Yes' : 'No';
      });
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    const timestamp = new Date().toISOString().split('T')[0];
    const filterSuffix = hasActiveFilters ? '_filtered' : '';
    link.setAttribute('download', `employee_documents_${timestamp}${filterSuffix}.csv`);
    
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const fetchDocuments = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/emp-documnets/get-documents`);
      const checkListData = await response.json();
      const document = Object.keys(checkListData);
      const data = Object.values(checkListData).map((item, index) => {
        return {
          eid: document[index].split('_')[0],
          employee_name: document[index].split('_').slice(1).join(' '),
          ...item
        }
      });
      const types = Object.keys(data[0]).filter(key => key !== 'eid' && key !== 'employee_name');
      setDocumentTypes(types.sort());
      const activeData = data.filter(item => item.isActive);
      const inactiveData = data.filter(item => !item.isActive); 
      setActiveDocuments(activeData);
      setInactiveDocuments(inactiveData);
      setDocuments(pathLocation.pathname === '/checklist/inactive' ? inactiveData : activeData);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if(pathLocation.pathname === '/checklist/inactive') {
      setDocuments(inactiveDocuments);
    } else {
      setDocuments(activeDocuments);
    }
  }, [pathLocation.pathname, activeDocuments, inactiveDocuments]);

  const handleFilterChange = (docType, filterType, checked) => {
    setFilters(prev => ({
      ...prev,
      [docType]: {
        ...prev[docType],
        [filterType]: checked
      }
    }));
    setCurrentPage(1);
  };

  const getFilteredDocuments = () => {
    let filtered = documents.filter(doc =>
      doc.eid.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.employee_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    Object.entries(filters).forEach(([docType, filter]) => {
      if (filter.hasDocument || filter.noDocument) {
        filtered = filtered.filter(doc => {
          const docValue = doc[docType];
          const hasDocument = Boolean(docValue);
          
          if (filter.hasDocument && filter.noDocument) {
            return true;
          } else if (filter.hasDocument) {
            return hasDocument;
          } else if (filter.noDocument) {
            return !hasDocument;
          }
          return true;
        });
      }
    });

    return filtered;
  };

  const filteredDocuments = getFilteredDocuments();

  const totalItems = filteredDocuments.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = filteredDocuments.slice(startIndex, endIndex);

  const hasActiveFilters = Object.values(filters).some(filter => 
    filter.hasDocument || filter.noDocument
  );

  const clearAllFilters = () => {
    const clearedFilters = {};
    documentTypes.forEach(type => {
      clearedFilters[type] = { hasDocument: false, noDocument: false };
    });
    setFilters(clearedFilters);
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleViewDocument = (url, docMetadata) => {
    setViewModal({ isOpen: true, url, docMetadata });
  };

  const handleUploadClick = (employeeId, documentType) => {
    setUploadModal({ isOpen: true, employeeId, documentType });
  };

  const handleUploadDocument = async (file) => {
    try {
      const formData = new FormData();
      formData.append("employeeId", uploadModal.employeeId);
      formData.append("documentType", uploadModal.documentType);
      formData.append("file", file);

      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/upload-document`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const result = await response.json();

      setDocuments((prevDocs) =>
        prevDocs.map((doc) => {
          if (doc.eid === uploadModal.employeeId) {
            return {
              ...doc,
              [uploadModal.documentType]: result.fileUrl || URL.createObjectURL(file), 
            };
          }
          return doc;
        })
      );
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };
  const handleDeleteDocument = (employeeId, documentType) => {
    setDocuments((prevDocs) =>
      prevDocs.map((doc) => {
        if (doc.eid === employeeId) {
          return {
            ...doc,
            [documentType]: "", 
          };
        }
        return doc;
      })
    );
  }

  const handleDownloadAllMergedDocuments = async (employee_key) => {
    try {
      setDownloadStatus({ loading: true, progress: 0, employee_key });

      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/emp-documnets/get-merged-pdf?employee_key=${employee_key.split(' ').join("_")}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const contentLength = response.headers.get("Content-Length");
      const total = contentLength ? parseInt(contentLength, 10) : 0;

      if (!total) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${employee_key.split(' ').join("_")}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        setDownloadStatus({ loading: false, progress: 100, employee_key });
        return;
      }

      const reader = response.body.getReader();
      const chunks = [];
      let receivedLength = 0;

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        chunks.push(value);
        receivedLength += value.length;

        const progress = Math.round((receivedLength / total) * 100);
        setDownloadStatus({ loading: true, progress, employee_key });
      }

      const blob = new Blob(chunks, { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.href = url;
      link.download = `${employee_key.split(' ').join("_")}.pdf`;
      link.click();
      URL.revokeObjectURL(url);

      setDownloadStatus({ loading: false, progress: 100, employee_key });

    } catch (error) {
      console.error("Error downloading file:", error);
      setDownloadStatus({ loading: false, progress: 0, employee_key: "" });
    } finally {
      setTimeout(() => {
        setDownloadStatus({ loading: false, progress: 0, employee_key: "" });
      }, 1000);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (!target.closest('.filter-dropdown')) {
        setOpenFilter(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, []);

  return (
    <div className="container mx-auto p-4">
      <ViewDocumentModal
        isOpen={viewModal.isOpen}
        onClose={() => setViewModal({ isOpen: false, url: '' })}
        documentUrl={viewModal.url}
        docMetadata={viewModal.docMetadata}
        setDocuments={handleDeleteDocument}
      />

      <UploadDocumentModal
        isOpen={uploadModal.isOpen}
        onClose={() => setUploadModal({ isOpen: false, employeeId: '', documentType: '' })}
        onUpload={handleUploadDocument}
        employeeId={uploadModal.employeeId}
        documentType={uploadModal.documentType}
      />
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 border-b">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <h1 className="text-xl font-semibold text-center md:text-left">
              Employee Document Status
            </h1>

            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="search"
                  placeholder="Search by Employee ID or Name..."
                  className="w-full pl-8 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <button
                onClick={exportToCSV}
                className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm w-full sm:w-auto flex items-center justify-center gap-2"
                disabled={totalItems === 0}
              >
                <FileDown className="h-4 w-4" />
                Export CSV
              </button>

              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="px-3 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 text-sm w-full sm:w-auto"
                >
                  Clear Filters
                </button>
              )}

              <button
                className="p-2 border rounded-md hover:bg-gray-50 disabled:opacity-50 flex justify-center w-full sm:w-auto"
                onClick={fetchDocuments}
                disabled={isLoading}
                aria-label="Refresh"
              >
                <RefreshCw className={`h-5 w-5 ${isLoading ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>
          
          {hasActiveFilters && (
            <div className="mt-3 pt-3 border-t">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm text-gray-600">Active filters:</span>
                {Object.entries(filters).map(([docType, filter]) => {
                  const activeFilters = [];
                  if (filter.hasDocument) activeFilters.push('Has Document');
                  if (filter.noDocument) activeFilters.push('No Document');
                  
                  if (activeFilters.length > 0) {
                    return (
                      <span
                        key={docType}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs"
                      >
                        {docType.split('_').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                        ).join(' ')}: {activeFilters.join(', ')}
                        <button
                          onClick={() => {
                            setFilters(prev => ({
                              ...prev,
                              [docType]: { hasDocument: false, noDocument: false }
                            }));
                          }}
                          className="ml-1 hover:text-blue-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          )}
        </div>
        
        <div className="border-t max-h-[calc(100vh-350px)] overflow-y-auto">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="lg:sticky left-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-[120px]">
                    Download All Merged Documents
                  </th>
                  <th className="lg:sticky left-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-[120px]">
                    Employee ID
                  </th>
                  <th className="lg:sticky left-[120px] z-10 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-[200px]">
                    Employee Name
                  </th>
                  {documentTypes.map((docType) =>docType !== 'isActive' && (
                    <th key={docType} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]">
                      <div className="flex flex-col items-center gap-2">
                        <span className="whitespace-nowrap">
                          {docType.split('_').map(word => 
                            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                          ).join(' ')}
                        </span>
                        <div className="filter-dropdown">
                          <FilterDropdown
                            documentType={docType}
                            filterState={filters[docType] || { hasDocument: false, noDocument: false }}
                            onFilterChange={handleFilterChange}
                            isOpen={openFilter === docType}
                            onToggle={() => setOpenFilter(openFilter === docType ? null : docType)}
                          />
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={documentTypes.length + 3} className="px-6 py-8 text-center text-sm text-gray-500">
                      <div className="flex items-center justify-center gap-2">
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Loading document status...
                      </div>
                    </td>
                  </tr>
                ) : currentPageData.length > 0 ? (
                  currentPageData.map((doc, index) => (
                    <tr key={doc.eid} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                      <td className="lg:sticky left-0 z-10 bg-gray-50 px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                        <button 
                          disabled={downloadStatus.loading && downloadStatus.employee_key === `${doc.eid}_${doc.employee_name}`} 
                          onClick={() => handleDownloadAllMergedDocuments(`${doc.eid}_${doc.employee_name}`)} 
                          title={`Download All ${doc.employee_name} Documents`} 
                          className="text-indigo-600 hover:text-indigo-900 w-full flex justify-center items-center bg-transparent border-none cursor-pointer p-0"
                        >
                          {downloadStatus.loading && downloadStatus.employee_key === `${doc.eid}_${doc.employee_name}` ? (
                            <><RefreshCw className="h-6 w-6 animate-spin" /> {downloadStatus.progress}%</>
                          ) : (
                            <DownloadIcon className="w-6 h-6" />
                          )}
                        </button>
                      </td>
                      <td className="lg:sticky left-0 z-10 bg-gray-50 px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                        {doc.eid}
                      </td>
                      <td className="lg:sticky left-[120px] z-10 bg-gray-50 px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                        {doc.employee_name}
                      </td>
                      {documentTypes.map((docType) => {
                        if (docType === 'isActive') return;
                        const docValue = doc[docType];
                        const hasDocument = Boolean(docValue);
                        const documentLink = docValue;
                        
                        return (
                          <td key={`${doc.eid}-${docType}`} className="px-4 py-4 whitespace-nowrap text-center">
                            {hasDocument ? (
                              <button
                                onClick={() => documentLink && handleViewDocument(documentLink, {eid: doc.eid, employee_key: `${doc.eid}_${doc.employee_name.split(' ').join('_')}`, docType: docType, documentLink: documentLink})}
                                className="text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed p-1 rounded hover:bg-blue-50 transition-colors"
                                disabled={!documentLink}
                                title="View document"
                              >
                                <Eye className="h-5 w-5 mx-auto" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleUploadClick(doc.eid, docType)}
                                className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100 transition-colors"
                                title="Upload document"
                              >
                                <Upload className="h-5 w-5 mx-auto" />
                              </button>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={documentTypes.length + 3} className="px-6 py-8 text-center text-sm text-gray-500">
                      {hasActiveFilters ? 'No documents found matching your search and filter criteria.' : 'No documents found matching your search.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>
    </div>
  );
}